import uuid from "react-uuid";
import { EllipsisVerticalIcon } from "@heroicons/react/24/outline";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import AuthenticationService from "../../services/AuthenticationService";
import WorksiteTaskActivity from "./WorksiteTaskActivity";

export const MainTaskActivitiesForm = ({
  activities,
  worksite,
  task,
  loading,
  onSubmit,
  onSkip,
  onCheck,
  onDelete,
  onDrop,
  editMode,
  activity_id = undefined,
  note_id = undefined,
}) => {
  const userData = AuthenticationService.getUserData() || {};
  const val_userdata = `${userData.firstname} ${userData.lastname}`;

  // Group activities that are checklists
  // So that they can't be interleaved between other custom activities
  // that are normally draggable
  const groupedActivities = activities?.items.reduce((acc, a, i) => {
    a.__key = uuid();
    a.__index = i;
    if (a.activity_type === "checklists") {
      if (!acc[acc.length - 1]?.isChecklistGroup) {
        acc.push({ isChecklistGroup: true, items: [a] });
      } else {
        acc[acc.length - 1].items.push(a);
      }
    } else {
      acc.push(a);
    }
    return acc;
  }, []);

  const handleActivityDrop = (dropped) => {
    const { source, destination } = dropped;
    // ignore drop outside droppable container
    if (!dropped.destination || !activities?.items.length) {
      return;
    }
    // remove dragged item
    const [reorderedItem] = groupedActivities.splice(source.index, 1);

    // add dropped item
    groupedActivities.splice(destination.index, 0, reorderedItem);

    // unflatten activities
    const updatedList = groupedActivities.reduce((acc, a) => {
      if (a.isChecklistGroup) {
        acc.push(...a.items);
      } else {
        acc.push(a);
      }
      return acc;
    }, []);

    onDrop(
      activities,
      updatedList.map((a, i) => ({ ...a, val_order: i }))
    );
  };

  const renderActivityCard = (a, i, provided) => {
    const isTemporary = !a.id || !a.val_text || !a.val_description;
    const isOwnerAssignee = task?.id_assignee === userData.id && a?.val_creator === val_userdata;
    return (
      <WorksiteTaskActivity
        title={a.val_text}
        description={a.val_description}
        worksite={worksite}
        isDraft={!a.id}
        editable={
          // todo check id_assignee for task and not val_creator
          (editMode || isTemporary || isOwnerAssignee) &&
          !a.flg_disabled &&
          !a.to_delete &&
          a.activity_type === "custom"
        }
        activityType={a.activity_type}
        canMarkAsDone={
          a.id !== undefined && task?.id_assignee && (task?.id_assignee === userData.id || a.closed_at)
        }
        activityId={a.id}
        skipped={a.flg_disabled}
        deleted={a.to_delete}
        checked={a.closed_at}
        completedBy={a.val_closed_by}
        completedOn={a.closed_at}
        createdBy={a.val_creator}
        onEditComplete={(content) => {
          onSubmit(content, a, i);
        }}
        onSkip={(skipped) => {
          onSkip(skipped, a, i);
        }}
        onCheck={(checked) => {
          onCheck(checked, a, i);
        }}
        onDelete={(to_delete) => {
          onDelete(to_delete, a, i);
        }}
        note_id={a.id === activity_id ? note_id : undefined}
      >
        {a.activity_type !== "checklists" && (
          <div
            className="flex items-center font-bold h-10 -mt-5 bg-gray-200 text-gray-500 py-1 px-0 rounded-xl absolute -left-2"
            {...provided.dragHandleProps}
          >
            <EllipsisVerticalIcon className="h-4" />
          </div>
        )}
      </WorksiteTaskActivity>
    );
  };

  return (
    <DragDropContext onDragEnd={handleActivityDrop}>
      <Droppable droppableId="activities-drop-area">
        {(provided) => (
          <div {...provided.droppableProps} ref={provided.innerRef}>
            {groupedActivities.map((groupOrActivity, i) => {
              const a = groupOrActivity;
              const key =
                a.id ||
                (a?.items || [])
                  .reduce((acc, cur) => {
                    return acc.concat(cur.id);
                  }, [])
                  .join("-") ||
                i;

              return groupOrActivity.isChecklistGroup ? (
                <Draggable
                  key={`activities-droppable-${key}`}
                  draggableId={`activities-droppable-${key}`}
                  index={i}
                  isDragDisabled={true}
                >
                  {(provided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                    >
                      {groupOrActivity.items.map((a, j) => {
                        const key = a.id || j;
                        return (
                          <div
                            className={[
                              "my-3 transition-opacity duration-400",
                              loading ? "opacity-50 pointer-events-none" : "",
                            ].join(" ")}
                            key={key}
                          >
                            {renderActivityCard(a, a.__index)}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </Draggable>
              ) : (
                <Draggable
                  key={`activities-droppable-${key}`}
                  draggableId={`activities-droppable-${key}`}
                  index={i}
                >
                  {(provided) => (
                    <div
                      className={[
                        "my-3 transition-opacity duration-400",
                        loading ? "opacity-50 pointer-events-none" : "",
                      ].join(" ")}
                      key={key}
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                    >
                      {renderActivityCard(a, a.__index, provided)}
                    </div>
                  )}
                </Draggable>
              );
            })}
            {provided.placeholder}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
};
